import { useFormik } from "formik";
import api from "../config/axiosConfig";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import toast from "react-hot-toast";
import { convertToFormData } from "../config/convertToFormData";
import { useSelector } from "react-redux";
import { hasPermission } from "../utils/utilityFunctions";
import { actionType } from "../utils/constant";

// ----- Constants & Validation -----
const API_URL = "/api/rfi-documents-v2/";
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const validationSchema = yup.object({
  remarks: yup.string().required("Remarks is required"),
  documents: yup
    .array()
    .min(1, "At least one document is required")
    .max(5, "Maximum 5 documents allowed"),
});

// ----- Helpers -----
const toAbsoluteUrl = (url) => {
  if (!url) return "";
  return /^https?:\/\//i.test(url) ? url : `${API_BASE}${url}`;
};

const mapDoc = (doc = {}) => ({
  document: toAbsoluteUrl(doc.file_url || doc.document_url || doc.url),
  created_at: doc.uploaded_at || doc.created_at,
  document_id: doc.document_id || doc.uploaded_document_id,
  uploaded_document_id: doc.uploaded_document_id || doc.document_id,
});

const normalizeDocumentsResponse = (data) => {
  let documents = [];
  if (Array.isArray(data?.documents)) {
    documents = data.documents.map(mapDoc);
  }
  const remarks = typeof data?.remarks === "string" ? data.remarks : undefined;
  return { documents, remarks };
};

export default function useNonDimentionalRFI() {
  const { rfinumber: rfi_id } = useParams();
  const { profile } = useSelector((state) => state.profile);

  const navigate = useNavigate();
  // Tracks whether this RFI has existing data or has been initialized once
  const [isUpdating, setIsUpdating] = useState(false);
  // Tracks loading state for initial fetch of existing data
  const [isFetching, setIsFetching] = useState(true);
  const formik = useFormik({
    initialValues: {
      remarks: "",
      documents: [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        if (!rfi_id) {
          toast.error("RFI ID not found");
          return;
        }
        if (
          !hasPermission(profile.permissions.actions, actionType.stage_edit)
        ) {
          toast.error("You don't have permission to save/edit RFI.");
          return;
        }

        // Build multipart/form-data payload
        const payload = convertToFormData({
          rfi_id,
          remarks: values.remarks,
          documents: values.documents, // array of File
        });

        if (isUpdating) {
          await api.put(API_URL, payload);
          toast.success("Documents updated successfully!");
        } else {
          await api.post(API_URL, payload);
          toast.success("Documents submitted successfully!");
        }
        // After first successful submission, mark as updating for future interactions
        setIsUpdating(true);

        // Redirect to dashboard on success
        navigate("/dashboard", { replace: true });
      } catch (error) {
        console.error("Failed to submit Non-Dimensional RFI:", error);
        toast.error("Failed to submit. Please try again.");
      }
    },
  });

  // Fetch existing documents and remarks on mount
  useEffect(() => {
    const fetchExisting = async () => {
      if (!rfi_id) return;
      if (!hasPermission(profile.permissions.actions, actionType.rfi_view))
        return;

      try {
        setIsFetching(true);
        const res = await api.get(`${API_URL}rfi/${rfi_id}`);
        const data = res?.data;

        const { documents: existingDocs, remarks } =
          normalizeDocumentsResponse(data);

        if (typeof remarks === "string") {
          formik.setFieldValue("remarks", remarks || "");
        }
        formik.setFieldValue("documents", existingDocs);

        // If we got any data back (remarks or documents), mark as updating
        if ((existingDocs && existingDocs.length > 0) || remarks) {
          setIsUpdating(true);
        }
      } catch (error) {
        console.error("Failed to load existing RFI documents:", error);
        // Non-blocking: user can still upload
      } finally {
        setIsFetching(false);
      }
    };

    fetchExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfi_id]);

  const removeFile = async (file, files, setFieldValue) => {
    // If it's a new file (File object), remove locally
    if (!hasPermission(profile.permissions.actions, actionType.stage_edit)) {
      toast.error("You don't have permission to remove files");
      return;
    }
    if (file?.name && !file?.document_id) {
      const updated = (files || []).filter((f) => f?.name !== file.name);
      setFieldValue("documents", updated);
      return;
    }

    // Existing file: call DELETE API if id is present
    const documentId = file?.document_id || file?.uploaded_document_id;
    if (!documentId) {
      // Fallback: remove by URL if no id present
      const updated = (files || []).filter(
        (f) => f?.document !== file?.document
      );
      setFieldValue("documents", updated);
      return;
    }

    try {
      await api.delete(`/api/rfi-documents-v2/${documentId}`);
      const updated = (files || []).filter((f) => {
        if (f?.document_id) return f.document_id !== documentId;
        if (f?.uploaded_document_id)
          return f.uploaded_document_id !== documentId;
        return f?.document !== file?.document;
      });
      setFieldValue("documents", updated);
      toast.success("Document deleted successfully");
    } catch (err) {
      console.error("Failed to delete document:", err);
      toast.error("Failed to delete document. Please try again.");
    }
  };
  const addFiles = (files, currentFiles, setFieldValue) => {
    if (!hasPermission(profile.permissions.actions, actionType.stage_edit)) {
      toast.error("You don't have permission to add files");
      return;
    }
    const MAX_FILES = 5;
    const existingCount = Array.isArray(currentFiles) ? currentFiles.length : 0;
    const remainingSlots = MAX_FILES - existingCount;

    if (remainingSlots <= 0) {
      toast.error("Maximum 5 documents allowed");
      return;
    }

    const filesArray = Array.isArray(files) ? files : Array.from(files || []);
    if (filesArray.length > remainingSlots) {
      toast.error("Maximum 5 documents allowed");
      return;
    }

    setFieldValue("documents", [...(currentFiles || []), ...filesArray]);
  };

  return {
    formik,
    removeFile,
    addFiles,
    isUpdating,
    isFetching,
    actions: profile.permissions.actions,
  };
}
